import React from "react";
import { Button } from "reactstrap";
import Select from "react-select";
import NavHeader from "./NavHeader";
import { fechaCalendario } from "./fechas";
import { importarFixture } from "services/torneosService";
import { toastBigError, toastConfirm } from "components/utils/toasts";

// react-select no toma clases en su control, hay que pintarle el borde a mano.
const estiloSelectInvalido = {
  control: (base) => ({ ...base, borderColor: "#ff3636" }),
};

// Una fecha de una zona: fecha + sede + Lista de Fe (asistencia) + Partidos.
// Las planillas en PDF (árbitros, micrófono, carteles) se generan desde la pantalla
// de la rueda, porque salen para toda la jornada y no para una zona sola.
// Fecha y sede son obligatorias: mientras falte alguna no se sale de la pantalla
// (el bloqueo real lo hace el host, que también intercepta el botón atrás del
// navegador; acá se marcan los campos en rojo).
// La sección activa se persiste en el host (section/onSectionChange) para que al
// entrar a un partido y volver, se mantenga seleccionada.
// numero: el "Fecha N" que le corresponde dentro de la zona (lo calcula el host).
//
// Todo lo de esta pantalla se guarda de a un pedazo, y por eso cada cosa avisa por su
// propio callback en vez de devolver la fecha entera: la cabecera (día y sede), la
// lista de fe de UN equipo, y el alta o la baja de UN partido. Es lo que permite que
// dos personas carguen la misma fecha desde dos celulares sin pisarse.
export default function DateScreen({
  date,
  numero,
  zoneTeamIds,
  teams,
  sedes = [],
  section = "attendance",
  onSectionChange,
  onCabeceraChange,
  onAsistenciaChange,
  onAddMatch,
  onRemoveMatch,
  onOpenMatch,
  onOpenImport,
  onBack,
}) {
  const faltaFecha = !date.date;
  const faltaSede = !date.sede;

  const importRef = React.useRef(null);
  const [isImporting, setIsImporting] = React.useState(false);
  // Lista de Fe: cada equipo arranca colapsado; se despliega al tocar su cabecera.
  const [equiposAbiertos, setEquiposAbiertos] = React.useState({});
  const toggleEquipo = (teamId) =>
    setEquiposAbiertos((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  const getTeamById = (teamId) => teams.find((t) => t.id === teamId);

  const setDateValue = (value) => onCabeceraChange({ date: value });
  const setSede = (option) => onCabeceraChange({ sede: option ? option.value : "" });

  const togglePlayerAttendance = (teamId, playerId) => {
    const teamAttendance = date.attendance[teamId] || [];
    if (teamAttendance.includes(playerId)) {
      onAsistenciaChange(teamId, teamAttendance.filter((id) => id !== playerId));
      return;
    }
    if (teamAttendance.length >= 10) return;
    onAsistenciaChange(teamId, [...teamAttendance, playerId]);
  };

  // Si el partido ya tenía el resultado cargado, se descarta junto con el partido.
  const removeMatch = (match) => {
    const cruce = teamName(match.homeTeamId) + " vs " + teamName(match.awayTeamId);
    const texto = match.jugado
      ? cruce + ". Se descarta el resultado ya cargado."
      : cruce + ".";

    toastConfirm("¿ Eliminar el partido ?", texto, () => onRemoveMatch(match));
  };

  // El Excel trae los partidos de todas las zonas de la jornada; el server sólo lo
  // lee y devuelve las filas. Qué partidos son de esta zona (y qué se hace con los
  // que no se reconocen) se decide en la pantalla de previsualización.
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsImporting(true);
    let data;
    try {
      data = await importarFixture(file);
    } finally {
      // Pase lo que pase, el velo de "Importando archivo..." se tiene que ir.
      setIsImporting(false);
    }
    // Permite volver a subir el mismo archivo (si no, el change no se dispara).
    e.target.value = "";
    if (data && data.success) onOpenImport(data.records);
    else toastBigError("No se pudo importar", (data && data.message) || "Revisá el archivo e intentá de nuevo.");
  };

  const headerTitle = numero ? "Fecha " + numero : "Fecha";
  const headerSubtitle = [fechaCalendario(date), date.matches.length + " partidos"].filter(Boolean).join(" · ");

  const teamName = (teamId) => {
    const t = getTeamById(teamId);
    return t ? t.name : "—";
  };

  // Partidos ordenados por hora; los sin hora van al final.
  const partidosOrdenados = [...date.matches].sort((a, b) => {
    if (!a.hora) return 1;
    if (!b.hora) return -1;
    return a.hora.localeCompare(b.hora);
  });

  return (
    <div className="screen-slide">
      {isImporting && (
        <div className="loading-overlay">
          <div className="d-flex flex-column justify-content-center align-items-center h-100">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <span className="mt-2">Importando archivo...</span>
          </div>
        </div>
      )}
      <NavHeader title={headerTitle} subtitle={headerSubtitle} onBack={onBack} />

      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-12 col-md-5 mb-3 mb-md-0">
              <div className="form-group mb-0">
                <label className="font-weight-bold" style={{ fontSize: "0.8125rem" }}>
                  Fecha <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className={"form-control" + (faltaFecha ? " campo-invalido" : "")}
                  value={date.date}
                  onChange={(e) => setDateValue(e.target.value)}
                />
                {faltaFecha && <div className="mensaje-obligatorio">La fecha es obligatoria</div>}
              </div>
            </div>
            <div className="col-12 col-md-7">
              <div className="form-group mb-0">
                <label className="font-weight-bold" style={{ fontSize: "0.8125rem" }}>
                  Sede <span className="text-danger">*</span>
                </label>
                <Select
                  isSearchable={true}
                  isClearable={true}
                  placeholder="Seleccioná una sede..."
                  classNamePrefix="react-select"
                  name="selectSede"
                  value={sedes.find((o) => o.value === date.sede) || null}
                  onChange={setSede}
                  options={sedes}
                  noOptionsMessage={() => "Sin sedes"}
                  styles={faltaSede ? estiloSelectInvalido : undefined}
                />
                {faltaSede && <div className="mensaje-obligatorio">La sede es obligatoria</div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex mb-3" style={{ gap: "0.5rem" }}>
        <Button
          className="btn-arhock btn-sm my-0"
          style={{ opacity: section === "matches" ? 1 : 0.55 }}
          onClick={() => onSectionChange("matches")}
        >
          Partidos
        </Button>
        <Button
          className="btn-arhock btn-sm my-0"
          style={{ opacity: section === "attendance" ? 1 : 0.55 }}
          onClick={() => onSectionChange("attendance")}
        >
          Lista de Fe
        </Button>
      </div>

      {section === "attendance" && (
        <div>
          <div className="sub-section-title">Lista de Fe (máx 10 por equipo)</div>
          {zoneTeamIds.map((teamId) => {
            const team = getTeamById(teamId);
            if (!team) return null;
            const teamAttendance = date.attendance[teamId] || [];
            const abierto = !!equiposAbiertos[teamId];
            return (
              <div className="card mb-2" key={teamId}>
                <div
                  className="card-header py-2 px-3 d-flex align-items-center justify-content-between"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleEquipo(teamId)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={abierto}
                  aria-label={(abierto ? "Colapsar" : "Desplegar") + " " + team.name}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleEquipo(teamId);
                    }
                  }}
                >
                  <span className="font-weight-bold d-flex align-items-center" style={{ fontSize: "0.875rem", gap: "0.375rem" }}>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      style={{
                        transform: abierto ? "rotate(90deg)" : "none",
                        transition: "transform 0.15s",
                        flexShrink: 0,
                      }}
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                    {team.name}
                  </span>
                  <span
                    className={"badge badge-pill " + (teamAttendance.length >= 10 ? "badge-danger" : "badge-success")}
                    style={{ fontSize: "0.6875rem" }}
                  >
                    {teamAttendance.length}/10 seleccionados
                  </span>
                </div>
                <div className="card-body py-2" style={{ display: abierto ? undefined : "none" }}>
                  {team.players.length === 0 ? (
                    <p className="text-muted mb-0" style={{ fontSize: "0.8125rem" }}>
                      El equipo no tiene jugadores cargados.
                    </p>
                  ) : (
                    <div className="d-flex flex-wrap" style={{ gap: "0.375rem" }}>
                      {team.players.map((player) => {
                        const isSelected = teamAttendance.includes(player.id);
                        const isDisabled = !isSelected && teamAttendance.length >= 10;
                        return (
                          <span
                            key={player.id}
                            className={
                              "player-chip" +
                              (isSelected ? " selected" : "") +
                              (player.isExcepcion ? " excepcion" : "")
                            }
                            style={{
                              opacity: isDisabled ? 0.5 : 1,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                            }}
                            onClick={() => {
                              if (!isDisabled) togglePlayerAttendance(teamId, player.id);
                            }}
                            role="button"
                            tabIndex={0}
                            title={player.isExcepcion ? "Jugador de excepción" : undefined}
                            aria-label={
                              (isSelected ? "Sacar" : "Agregar") +
                              " " +
                              (player.name || "Sin Nombre") +
                              (player.isExcepcion ? " (excepción)" : "")
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                if (!isDisabled) togglePlayerAttendance(teamId, player.id);
                              }
                            }}
                          >
                            {isSelected && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                            {player.camiseta ? "#" + player.camiseta + " " : ""}
                            {player.name || "Sin Nombre"}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {section === "matches" && (
        <div>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="sub-section-title mb-0">Partidos</div>
            <div className="d-flex flex-wrap justify-content-end" style={{ gap: "0.5rem" }}>
              <Button
                className="btn-arhock btn-sm my-0"
                onClick={() => importRef.current.click()}
                disabled={zoneTeamIds.length < 2 || isImporting}
              >
                {isImporting ? "Leyendo..." : "Importar Partidos"}
              </Button>
              <Button className="btn-arhock btn-sm my-0" onClick={onAddMatch} disabled={zoneTeamIds.length < 2}>
                + Agregar Partido
              </Button>
            </div>
            <input
              type="file"
              className="d-none"
              ref={importRef}
              accept=".xlsx, .xls"
              onChange={handleFileChange}
            />
          </div>

          {zoneTeamIds.length < 2 ? (
            <p className="text-muted" style={{ fontSize: "0.8125rem" }}>
              Necesitás al menos 2 equipos en la zona para cargar partidos.
            </p>
          ) : date.matches.length === 0 ? (
            <p className="text-muted text-center py-2" style={{ fontSize: "0.875rem" }}>
              Todavía no hay partidos. Tocá &quot;Agregar Partido&quot; para empezar a cargar resultados.
            </p>
          ) : (
            partidosOrdenados.map((m) => (
              <div
                key={m.id}
                className="drill-row drill-row--match match-row"
                onClick={() => onOpenMatch(m.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpenMatch(m.id);
                  }
                }}
              >
                <div className="match-row-main">
                  <span className="match-row-hora">{m.hora || "—"}</span>
                  <span className="match-row-teams">
                    {teamName(m.homeTeamId)} vs {teamName(m.awayTeamId)}
                  </span>
                  <span className="match-row-cancha">{m.cancha ? "Cancha " + m.cancha : "—"}</span>
                </div>
                <div className="match-row-actions">
                  <button
                    type="button"
                    className="drill-row-delete"
                    aria-label="Eliminar partido"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMatch(m);
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6M14 11v6" />
                    </svg>
                  </button>
                  <svg className="drill-row-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
